import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { BADGE_ICONS } from './icons';
import { TIER_STYLES, TIER_TOOLTIP_COLORS } from './badgeConstants';

interface BadgeChipProps {
  badgeKey: string;
  name: string;
  description: string;
  level: 1 | 2 | 3 | 4 | 5;
  emoji?: string;
  svgKey?: string;
  earnedAt?: string;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
}

const SIZE_CONFIG = {
  sm: {
    chip: 'gap-1 px-1.5 py-0.5 rounded',
    icon: 'w-4 h-4 rounded-sm p-[2px]',
    text: 'text-[9px] leading-tight font-semibold',
    svg: 'w-3 h-3',
    emoji: 'text-[10px]',
  },
  md: {
    chip: 'gap-1.5 px-2 py-1 rounded-md',
    icon: 'w-6 h-6 rounded p-[3px]',
    text: 'text-[11px] leading-tight font-semibold',
    svg: 'w-4 h-4',
    emoji: 'text-sm',
  },
  lg: {
    chip: 'gap-2 px-3 py-2 rounded-lg',
    icon: 'w-10 h-10 rounded-md p-1.5',
    text: 'text-sm leading-tight font-semibold',
    svg: 'w-6 h-6',
    emoji: 'text-xl',
  },
} as const;

function formatEarnedDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function BadgeChip({
  badgeKey,
  name,
  description,
  level,
  emoji,
  svgKey,
  earnedAt,
  size = 'md',
  showTooltip = true,
}: BadgeChipProps) {
  const tierStyle = TIER_STYLES[level];
  const sizeConfig = SIZE_CONFIG[size];
  const IconComponent = svgKey ? BADGE_ICONS[svgKey] : null;

  const chip = (
    <div
      data-badge={badgeKey}
      className={cn('inline-flex items-center select-none', tierStyle.chip, sizeConfig.chip)}
    >
      <div
        className={cn('flex items-center justify-center shrink-0', tierStyle.icon, sizeConfig.icon)}
        aria-hidden="true"
      >
        {IconComponent ? (
          <div className={sizeConfig.svg}>
            <IconComponent />
          </div>
        ) : emoji ? (
          <span className={sizeConfig.emoji}>{emoji}</span>
        ) : null}
      </div>
      <span className={sizeConfig.text}>{name}</span>
    </div>
  );

  if (!showTooltip) return chip;

  const tooltipColor = TIER_TOOLTIP_COLORS[level];

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{chip}</TooltipTrigger>
        <TooltipContent
          className="max-w-[280px] text-center"
          style={{
            background: '#2C2416',
            color: '#F5F0E8',
            borderRadius: '4px',
            padding: '8px 12px',
            fontSize: '11px',
            fontFamily: "'Source Sans 3', sans-serif",
          }}
        >
          <p className="font-bold text-xs" style={{ color: tooltipColor }}>
            {name}
          </p>
          <p className="mt-1" style={{ color: '#D4C5A9' }}>
            {description}
          </p>
          {earnedAt && (
            <p className="mt-1 text-[10px]" style={{ color: '#9C8E7C' }}>
              Earned {formatEarnedDate(earnedAt)}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
